import { Resources } from 'constants/resources';

import { createContext, useContext, useMemo } from 'react';

import { useEnableAdditionalFields } from 'pages/GatewayRules/hooks/useEnableAdditionalFields';
import { useGetList } from 'react-admin';
import { useWatch } from 'react-hook-form';
import { Method } from 'types/embedded/method';
import { getId } from 'utils';

type GatewayRulesMerchantDictionaryData = {
  merchantId?: string;
  enableAdditionalFields: boolean;
  methods: Method[];
  isLoadingMethods: boolean;
};

const GatewayRulesMerchantDictionaryContext =
  createContext<GatewayRulesMerchantDictionaryData | null>(null);

export function GatewayRulesMerchantDictionaryProvider({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  const merchant = useWatch({ name: 'merchant' });
  const merchantId: string | undefined = merchant
    ? typeof merchant === 'object'
      ? getId(merchant['@id'])
      : merchant
    : undefined;

  const { enableAdditionalFields } = useEnableAdditionalFields(merchantId);

  const { data: methods = [], isLoading: isLoadingMethods } = useGetList<Method>(
    Resources.Methods,
    {
      pagination: { page: 1, perPage: 1000 },
      filter: { groups: ['reference'], merchant: [merchantId] },
    },
    { enabled: !!merchantId },
  );

  const value = useMemo(
    () => ({ merchantId, methods, isLoadingMethods, enableAdditionalFields }),
    [merchantId, methods, isLoadingMethods, enableAdditionalFields],
  );

  return (
    <GatewayRulesMerchantDictionaryContext.Provider value={value}>
      {children}
    </GatewayRulesMerchantDictionaryContext.Provider>
  );
}

export function useGatewayRulesMerchantDictionaryContext(): GatewayRulesMerchantDictionaryData {
  const context = useContext(GatewayRulesMerchantDictionaryContext);
  if (!context) {
    throw new Error(
      'Can not `useGatewayRulesMerchantDictionary` outside of `GatewayRulesMerchantDictionaryProvider`',
    );
  }
  return context;
}
